import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, generatePath, useLocation, useSearchParams } from 'react-router-dom';

import { Button, Divider, backgroundImage, logoTrz } from '@treezor/design-system';

import { Helmet } from 'react-helmet';

import { Loader } from '@/ui-kit/loader/Loader';
import { APP_BUILD, APP_VERSION } from '@/appVersion';
import { useAppDispatch } from '@/hooks/redux-helpers';
import { useCurrentSupportUser } from '@/features/dashboard-users/hooks/useCurrentSupportUser';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';

import { fetchAccessToken, setAccessToken } from '@/store/actions/auth.actions';
import { TResponseType } from '@/store/models/dashboard-config.model';

import { useAccesControl } from '@/hooks/useAccessControl';
import { getAccessControlConfig } from '@/features/roles/store/actions/accessControl.actions';
import { useRoutepaths } from '@/router/hooks/useRoutePaths';

import { SignInNavigation } from './SignInNavigation';

export function SignIn() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const config = useDashboardConfig();
  const PathRoute = useRoutepaths();
  const { isAuthenticated, hasPassphrase, has2FAEnabled, isSSO } = useCurrentSupportUser();
  const { config: rolesConfig, role: advancedRole } = useAccesControl();

  const [searchParams] = useSearchParams();
  const { hash } = useLocation();

  const code = searchParams.get('code');

  const ssoAccessToken = new URLSearchParams(hash?.substring(1))?.get('access_token');

  const [isLoading, setIsLoading] = useState<Record<TResponseType, boolean>>({
    code: false,
    token: false,
  });

  useEffect(() => {
    if (code && !isAuthenticated) {
      dispatch(fetchAccessToken(code));
    }
  }, [code, isAuthenticated, dispatch]);

  useEffect(() => {
    const onAccessToken = async () => {
      dispatch(setAccessToken({ access_token: ssoAccessToken }));
      return;
    };
    if (ssoAccessToken && rolesConfig) {
      onAccessToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssoAccessToken, dispatch]);

  useEffect(() => {
    dispatch(getAccessControlConfig());
  }, [dispatch]);

  const navigateToSSO = (responseType: TResponseType) => {
    const ssoUrl =
      config &&
      `${config.api.sso.path}?response_type=${responseType}` +
        `&client_id=${config.api.sso.clientId}&redirect_uri=${config.api.sso.redirectUri}` +
        `&scope=${config.api.sso.scopes.join(' ')}&state=dashboard`;
    if (ssoUrl) {
      window.location.href = encodeURI(ssoUrl);
      setIsLoading({ ...isLoading, [responseType]: true });
    }
  };

  const dictDescription = {
    code: t('pages.auth.signIn.treezor'),
    token: t('pages.auth.signIn.company'),
    tokenOrCode: t('pages.auth.signIn.treezorOrCompany'),
  };

  if (isAuthenticated && advancedRole && rolesConfig) {
    if (has2FAEnabled && !ssoAccessToken) {
      return <Navigate to={generatePath(PathRoute.auth.root, { '*': PathRoute.auth['2fa'] })} />;
    }

    return <SignInNavigation hasPassphrase={hasPassphrase} />;
  }

  if (code) return <Loader />;

  return (
    <div style={{ backgroundImage: `url(${backgroundImage})` }} className="w-full h-full flex bg-cover">
      <Helmet title="Sign In" />
      <div className="w-fit m-auto">
        <img className="block m-auto w-[220px]" alt="Treezor Connect" src={logoTrz} />
        <div className="flex flex-col items-center bg-primary p-8 rounded-100 shadow-100 gap-5">
          <h4 className="typo-h4-bold text-secondary">{t('pages.auth.signIn.subtitle')}</h4>
          <div className="flex flex-col items-center">
            <span className="typo-body-regular-small text-secondary">
              {isSSO && config.site.environment.toLowerCase() === 'sandbox'
                ? dictDescription.tokenOrCode
                : dictDescription[config?.api.sso.responseType ?? 'code']}
            </span>
            <span className="typo-body-regular-small text-secondary">
              v{APP_VERSION} (Build {APP_BUILD})
            </span>
          </div>

          <div>
            {config && (
              <Button
                id={isSSO ? 'sign-in-company-btn' : 'sign-in-treezor-btn'}
                isLoading={isLoading[config.api.sso.responseType]}
                fullWidth
                onClick={() => navigateToSSO(config.api.sso.responseType)}
              >
                {isSSO ? t('pages.auth.signInCompanyBtn') : t('pages.auth.signInTreezorBtn')}
              </Button>
            )}

            {isSSO && config.site.environment.toLowerCase() === 'sandbox' && (
              <>
                <div className="flex">
                  <Divider className="my-6" />
                  <span className="flex items-center p-4 typo-body-regular-caption text-secondary">
                    {t('pages.auth.or')}
                  </span>
                  <Divider className="my-6" />
                </div>
                <Button
                  id="sign-in-treezor-btn"
                  variant="secondary"
                  isLoading={isLoading.code}
                  fullWidth
                  onClick={() => navigateToSSO('code')}
                >
                  {t('pages.auth.signInTreezorBtn')}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
